/**
 * Centralized service for handling question option translations
 */

// Translation key mappings for different question types
export const TRANSLATION_MAPPINGS = {
	fertilityPath: {
		"Trying naturally": "tryingNaturally",
		IVF: "ivf",
		IUI: "iui",
		"Fertility testing": "fertilityTesting",
		"Egg freezing": "eggFreezing",
		"Sperm freezing": "spermFreezing",
		Surrogacy: "surrogacy",
		Adoption: "adoption",
		"Egg Donation": "eggDonation",
		"Egg Preservation Freezing": "eggPreservationFreezing",
		"Embryo Preservation Freezing": "embryoPreservationFreezing",
		Pregnancy: "pregnancy",
		"Reciprocal IVF": "reciprocalIvf",
		"Sperm Donation": "spermDonation",
		Other: "other",
	},
	interests: {
		"Explore my path to parenthood": "explorePath",
		"Find a donor": "findDonor",
		"Plan my fertility journey": "planJourney",
		"Find a clinic or expert": "findClinic",
		"Find a surrogate": "findSurrogate",
		"Store and organize my documents": "storeDocuments",
		"Learn more about fertility": "learnFertility",
		"Get emotional support": "emotionalSupport",
		"Get a personalized fertility timeline": "personalizedTimeline",
		"Get legal guidance": "legalGuidance",
		"Explore and get financial support": "financialSupport",
		"I'm just exploring for now": "justExploring",
	},
	genderOptions: {
		Male: "male",
		Female: "female",
		"Non-binary / other": "nonBinary",
		"Prefer not to say": "preferNotToSay",
	},
	relationshipOptions: {
		"I'm a single parent by choice": "singleParent",
		"I'm in a heterosexual couple": "heterosexualCouple",
		"I'm in a same-sex female couple": "sameSexFemaleCouple",
		"I'm in a same-sex male couple": "sameSexMaleCouple",
	},
	profileOptions: {
		b2c: "intendedParent",
		b2b: "fertilityProvider",
		partner: "supportingDescription",
	},
} as const;

/**
 * Get translated label for a question option value
 */
export function getTranslatedLabel(
	questionType: keyof typeof TRANSLATION_MAPPINGS,
	value: string,
	t: (key: string) => string,
	fallback?: string,
): string {
	const mapping = TRANSLATION_MAPPINGS[questionType];
	if (!mapping) {
		console.warn(
			`No translation mapping found for question type: ${questionType}`,
		);
		return fallback || value;
	}

	const translationKey = mapping[value as keyof typeof mapping];
	if (!translationKey) {
		console.warn(
			`No translation key found for value: ${value} in ${questionType}`,
		);
		return fallback || value;
	}

	const translationPath = getTranslationPath(questionType, translationKey);
	const translated = t(translationPath);

	// If translation returns the same key (not found), return fallback or value
	return translated === translationPath ? fallback || value : translated;
}

/**
 * Get the full translation path for a question type and key
 */
function getTranslationPath(
	questionType: keyof typeof TRANSLATION_MAPPINGS,
	translationKey: string,
): string {
	switch (questionType) {
		case "fertilityPath":
			return `questions.fertilityPath.pathOptions.${translationKey}`;
		case "interests":
			return `questions.interests.options.${translationKey}`;
		case "genderOptions":
			return `questions.genderRelationship.genderOptions.${translationKey}`;
		case "relationshipOptions":
			return `questions.genderRelationship.relationshipOptions.${translationKey}`;
		case "profileOptions":
			return `questions.profileSelect.options.${translationKey}`;
		default:
			return translationKey;
	}
}

/**
 * Get all available options with translations for a question type
 */
export function getTranslatedOptions(
	questionType: keyof typeof TRANSLATION_MAPPINGS,
	t: (key: string) => string,
): Array<{ value: string; label: string }> {
	const mapping = TRANSLATION_MAPPINGS[questionType];
	if (!mapping) {
		return [];
	}

	return Object.keys(mapping).map((value) => ({
		value,
		label: getTranslatedLabel(questionType, value, t),
	}));
}

/**
 * Validate if a value exists in the translation mapping
 */
export function isValidOptionValue(
	questionType: keyof typeof TRANSLATION_MAPPINGS,
	value: string,
): boolean {
	const mapping = TRANSLATION_MAPPINGS[questionType];
	return mapping ? Object.hasOwn(mapping, value) : false;
}
